// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import Tooltip from "@insite/mobius/Tooltip";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { xIsGuest } from "@vanmeter/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@vanmeter/App/Store/Selectors/SettingsSelectors";
import ProductAddToCartButton from "@vanmeter/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered from "@vanmeter/Overrides/Components/ProductQuantityOrdered";
import { parseBoolean } from "@vanmeter/XDL/Utilities";
import { mergeWithCss } from "@vanmeter/XDL/Utilities/mergeWithCss";
import * as React from "react";
import { FC, useState } from "react";
import { shallowEqual } from "react-redux";

export interface ProductActionsProps {
    carouselId?: string;
    iconOnly?: boolean;
    showActions?: boolean;
    alwaysShowQty?: boolean;
    extendedStyles?: ProductActionsStyle;
    onAddToCartClick?: () => void;
    currentPage?: string;
    showDisabled?: boolean;
    hideUOM?: boolean;
    showMultipleSaleQty?: boolean;
    isPdp?: boolean;
}

type Props = ProductActionsProps & HasProductContext;

export interface ProductActionsStyle {
    addToCartContainer?: GridContainerProps;
    qtyWrapper?: GridItemProps;
    addToCartButtonWrapper?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    uomSelectWrapper?: GridItemProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    multipleSaleQty?: InjectableCss;
    wrapper?: InjectableCss;
    addToCartFallbackMessage?: TypographyPresentationProps;
}

export const productActionsStyle: ProductActionsStyle = {};

const ProductActions: FC<Props> = ({
    productContext,
    carouselId,
    extendedStyles,
    onAddToCartClick,
    iconOnly,
    hideUOM,
    showMultipleSaleQty = false,
    isPdp,
}) => {
    const [styles] = useState(() => mergeWithCss(productActionsStyle, extendedStyles));
    const uomRef = React.useRef<HTMLDivElement>(null);

    const history = useHistory();

    const {
        productSettings,
        configurationSelection,
        configurationCompleted,
        variantSelectionCompleted,
        subscription,
        isGuest,
    } = useAppSelector(
        state => ({
            productSettings: getSettingsCollection(state).productSettings,
            configurationSelection: state.pages.productDetails.configurationSelection,
            configurationCompleted: state.pages.productDetails.configurationCompleted,
            variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
            subscription: state.pages.productDetails.subscription,
            isGuest: xIsGuest(state),
        }),
        shallowEqual,
    );

    const product = productContext.product;
    const productInfo = productContext.productInfo;
    const { unitOfMeasures } = product;
    const isVariantParent = product.isVariantParent ?? parseBoolean(product.properties?.isVariantParent);
    const willShowUnitOfMeasureSelect =
        !hideUOM &&
        !(
            !productSettings?.alternateUnitsOfMeasure ||
            !unitOfMeasures ||
            unitOfMeasures.filter(uom => uom.unitOfMeasure).length <= 1
        );

    // if (!isAuthenticated) {
    //     return (
    //         <Typography {...styles.addToCartFallbackMessage}>
    //             {siteMessage(isPDP ? "Custom_PDP_SignIn_AddToCart" : "Custom_SignIn_AddToCart")}
    //         </Typography>
    //     );
    // }

    const basePdpProps = isPdp ? { configurationSelection, configurationCompleted, variantSelectionCompleted } : {};
    const qtyOrderedProps = basePdpProps;
    const productAddToCartButtonProps = { ...basePdpProps, ...(isPdp && { subscription }) };

    const xisGuest = isGuest && parseBoolean(product.properties?.guestViewOnly);
    // XNG-Changes: VMI - Classic
    if (!isPdp && isVariantParent) {
        return (
            <StyledWrapper {...styles.wrapper} data-test-selector="product_actions">
                <Button
                    {...styles.productAddToCartButtonStyles}
                    onClick={() => history.push(productInfo.productDetailPath)}
                    aria-label={translate("View Details")}
                >
                    {translate("View Details")}
                </Button>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.addToCartContainer} data-test-selector="product_actions" {...{ iconOnly }}>
                <StyledWrapper {...styles.qtyWrapper}>
                    <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                        {translate("QTY")}
                    </VisuallyHidden>
                    <ProductQuantityOrdered
                        labelOverride=""
                        data-test-selector="productQty"
                        disabled={xisGuest}
                        {...qtyOrderedProps}
                    />
                    {/* XNG-Changes: VMI - Classic */}
                    {xisGuest && <Tooltip text={siteMessage("Guest_User_Tooltip") as string} />}
                </StyledWrapper>
                {willShowUnitOfMeasureSelect && (
                    <StyledWrapper {...styles.uomSelectWrapper} ref={uomRef}>
                        <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
                    </StyledWrapper>
                )}
                {isPdp && showMultipleSaleQty && (
                    <StyledWrapper {...styles.multipleSaleQty}>
                        {createWidgetElement("ProductDetails/ProductMultipleSaleQty", {
                            fields: {},
                        })}
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.addToCartButtonWrapper} {...{ hasUom: !!uomRef.current, iconOnly }}>
                    <ProductAddToCartButton
                        onAddToCartClick={onAddToCartClick}
                        extendedStyles={styles.productAddToCartButtonStyles}
                        data-test-selector="addToCartBtn"
                        {...{ iconOnly }}
                        {...productAddToCartButtonProps}
                    />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProductContext(ProductActions);
